import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { ProvisioningMonthlyTotal, ProvisioningReport, ProvisioningReportRow } from 'src/app/models/dtos/provisioning-report';
import { ProductType } from 'src/app/models/enums';
import { Currency } from 'src/app/models/enums/currency';
import { CurrencyService } from 'src/app/services/currency.service';
import { CustomerService } from 'src/app/services/customer.service';
import { ProductService } from 'src/app/services/product.service';
import { TuitionService } from 'src/app/services/tuition.service';
import { MessageUtil } from 'src/app/utils/message.util';
import { StorageUtil } from 'src/app/utils/storage.util';
import {
  filterProvisioningRows,
  provisioningDayLabel,
  provisioningMonthLabel,
  provisioningRange,
  provisioningTotals,
} from './provisioning-report.util';

/**
 * ICES-39 — Faturamento > Relatórios > Provisionamento de Receitas Fixas: projeção mês a mês da receita
 * dos serviços periódicos de cliente (títulos de propriedade parcelados), inclusive dos meses cujas
 * faturas ainda não foram geradas. Uma chamada (POST /secure/tuition/provisioning-report) traz todas as
 * linhas do período; período e serviço vão para o backend, cliente e título filtram no front.
 */
@Component({
  selector: 'app-provisioning-report',
  templateUrl: './provisioning-report.component.html',
  styleUrls: ['./provisioning-report.component.scss']
})
export class ProvisioningReportComponent implements OnInit, OnDestroy {
  filterForm: FormGroup;
  report: ProvisioningReport = null;
  rows: ProvisioningReportRow[] = [];
  monthlyTotals: ProvisioningMonthlyTotal[] = [];
  periodTotal = 0;
  loading = false;
  hasSearched = false;
  pageSize = 10;
  currentCurrency: Currency = Currency.BRL;
  serviceOptions: { label: string, value: number }[] = [];
  customerOptions: { label: string, value: number }[] = [];

  readonly monthLabel = provisioningMonthLabel;
  readonly dayLabel = provisioningDayLabel;

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private tuitionService: TuitionService,
    private productService: ProductService,
    private customerService: CustomerService,
    private spinner: NgxSpinnerService,
    private messageUtil: MessageUtil,
    private currencyService: CurrencyService
  ) {
    const defaults = this.defaultFilters();
    this.filterForm = this.fb.group({
      startMonth: [defaults.startMonth],
      endMonth: [defaults.endMonth],
      serviceIds: [defaults.serviceIds],
      customerIds: [defaults.customerIds],
      titleNumber: [defaults.titleNumber],
    });
  }

  ngOnInit(): void {
    this.currentCurrency = this.currencyService.getCurrentCurrency();
    this.loadServices();
    this.loadCustomers();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Mês atual até 5 meses à frente, todos os serviços, clientes e títulos. */
  private defaultFilters(): { startMonth: Date, endMonth: Date, serviceIds: number[], customerIds: number[], titleNumber: string } {
    const now = new Date();
    return {
      startMonth: new Date(now.getFullYear(), now.getMonth(), 1),
      endMonth: new Date(now.getFullYear(), now.getMonth() + 5, 1),
      serviceIds: [],
      customerIds: [],
      titleNumber: '',
    };
  }

  /** Só os serviços periódicos de cliente (receitas fixas), sem IDs fixos. */
  loadServices(): void {
    this.productService.getAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (products) => {
          this.serviceOptions = (products || [])
            .filter((p) => p.productType === ProductType.PeriodicalServiceCustomer)
            .map((p) => ({ label: p.name, value: p.id }))
            .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
        },
        error: (error) => {
          console.error('Erro ao carregar os serviços:', error);
          this.serviceOptions = [];
        }
      });
  }

  loadCustomers(): void {
    this.customerService.getCustomerFilterList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (customers) => {
          this.customerOptions = (customers || []).map((c) => ({ label: c.name, value: c.id }));
        },
        error: (error) => {
          console.error('Erro ao carregar os clientes:', error);
          this.customerOptions = [];
        }
      });
  }

  searchData(): void {
    const { startMonth, endMonth, serviceIds } = this.filterForm.value;
    if (!startMonth || !endMonth) {
      this.messageUtil.generateMessage(MessageUtil.WARNING, 'SUMMARY.WARNING', 'Informe o mês inicial e o mês final.');
      return;
    }
    const range = provisioningRange(startMonth, endMonth);
    if (range.fromMs > range.toMs) {
      this.messageUtil.generateMessage(MessageUtil.WARNING, 'SUMMARY.WARNING', 'O mês inicial não pode ser posterior ao mês final.');
      return;
    }

    this.loading = true;
    this.spinner.show();
    this.tuitionService.getProvisioningReport({ ...range, serviceIds: (serviceIds || []).filter((id) => id != null) })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (report) => {
          this.report = report || { rows: [], monthlyTotals: [] };
          this.hasSearched = true;
          this.applyFilters();
          this.loading = false;
          this.spinner.hide();
        },
        error: (error) => {
          this.loading = false;
          this.spinner.hide();
          this.showError(error);
        }
      });
  }

  /** Cliente e título sobre as linhas já carregadas, e recálculo dos totais. */
  applyFilters(): void {
    const { customerIds, titleNumber } = this.filterForm.value;
    this.rows = filterProvisioningRows(this.report?.rows || [], customerIds, titleNumber);
    const totals = provisioningTotals(this.rows, this.report?.monthlyTotals || []);
    this.monthlyTotals = totals.monthlyTotals;
    this.periodTotal = totals.periodTotal;
  }

  clearFilters(): void {
    this.filterForm.reset(this.defaultFilters());
    this.report = null;
    this.rows = [];
    this.monthlyTotals = [];
    this.periodTotal = 0;
    this.hasSearched = false;
  }

  /** Linhas da planilha: uma por parcela provisionada, depois os totais por mês e o do período. */
  exportSheetRows(): any[] {
    const sheet: any[] = this.rows.map((row) => ({
      'Cliente': row.customerName || '',
      'Título': row.titleNumber ?? '',
      'Serviço': row.productName || '',
      'Início do Contrato': provisioningDayLabel(row.contractStartDate),
      'Fim do Contrato': provisioningDayLabel(row.contractEndDate),
      'Qtd. Parcelas': row.installmentCount ?? '',
      'Valor da Parcela': row.installmentValue ?? '',
      'Mês de Referência': provisioningMonthLabel(row.referenceMonth),
      'Valor Provisionado': row.provisionedValue ?? 0,
    }));
    sheet.push({});
    this.monthlyTotals.forEach((month) => sheet.push({
      'Cliente': 'Total ' + provisioningMonthLabel(month.referenceMonth),
      'Valor Provisionado': month.total,
    }));
    sheet.push({ 'Cliente': 'Total do período', 'Valor Provisionado': this.periodTotal });
    return sheet;
  }

  exportExcel(): void {
    if (this.loading || this.rows.length === 0) {
      return;
    }
    import('xlsx').then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(this.exportSheetRows());
      worksheet['!cols'] = [
        { wch: 35 }, // Cliente
        { wch: 10 }, // Título
        { wch: 30 }, // Serviço
        { wch: 18 }, // Início do Contrato
        { wch: 18 }, // Fim do Contrato
        { wch: 14 }, // Qtd. Parcelas
        { wch: 18 }, // Valor da Parcela
        { wch: 18 }, // Mês de Referência
        { wch: 20 }  // Valor Provisionado
      ];
      const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
      const excelBuffer: any = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
      const date = moment(new Date()).utc(true).format('DD-MM-YYYY');
      const marina = StorageUtil.getMarinaName();
      this.saveAsExcelFile(excelBuffer, `provisionamento_receitas_fixas_${date}_${marina}`);
    }).catch((err) => {
      console.error('Erro ao preparar XLSX para exportação:', err);
      this.messageUtil.generateMessage(MessageUtil.ERROR, 'SUMMARY.ERROR', 'Não foi possível exportar o relatório.');
    });
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const EXCEL_EXTENSION = '.xlsx';
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE
    });
    saveAs(data, fileName + EXCEL_EXTENSION);
  }

  /** 401/403 já são tratados pelo interceptor; o resto mostra a exceção do backend (data.exception). */
  private showError(error: any): void {
    if (error?.status === 401 || error?.status === 403) {
      return;
    }
    const exception = error?.error?.data?.exception;
    if (exception?.message) {
      this.messageUtil.generateMessage(exception.type || MessageUtil.ERROR, exception.title || 'SUMMARY.ERROR', exception.message);
      return;
    }
    this.messageUtil.generateMessage(
      MessageUtil.ERROR,
      'SUMMARY.ERROR',
      error?.error?.message || 'Não foi possível gerar o relatório de provisionamento.'
    );
  }
}
